import { z } from "zod";

/**
 * Creates a Zod schema for validating username updates.
 * @param {string} currentUsername - The user's existing username to prevent duplicates.
 * @returns {z.ZodObject} Validation schema for the edit username form.
 */
export const createEditUsernameSchema = (currentUsername = "") => {
  const normalizedCurrent = (currentUsername || "").trim().toLowerCase();

  return z.object({
    username: z
      .string({ required_error: "Please enter a username" })
      .trim()
      .min(3, "Username must be at least 3 characters")
      .max(30, "Username must be less than 30 characters")
      .regex(/^[a-zA-Z0-9]+$/, "Username can only contain letters and numbers")
      .superRefine((value, ctx) => {
        if (!value) {
          return;
        }

        if (
          normalizedCurrent &&
          value.trim().toLowerCase() === normalizedCurrent
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "New username must be different from current username",
          });
        }
      }),
  });
};


/**
 * Creates a Zod schema for validating password updates.
 * @returns {z.ZodObject} Validation schema for the edit password form.
 */
export const createEditPasswordSchema = () => {
  return z
    .object({
      currentPassword: z
        .string({ required_error: "Please enter your current password" })
        .trim()
        .min(1, "Current password is required"),
      newPassword: z
        .string({ required_error: "Please enter a new password" })
        .trim()
        .min(6, "Password must be at least 6 characters long")
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .regex(/\d/, "Password must contain at least one number")
        .regex(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        ),
      confirmPassword: z
        .string({ required_error: "Please confirm your new password" })
        .trim()
        .min(1, "Please confirm your new password"),
    })
    .superRefine(({ currentPassword, newPassword, confirmPassword }, ctx) => {
      if (newPassword !== confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["confirmPassword"],
          message: "New password and confirmation password do not match",
        });
      }

      if (currentPassword && newPassword && currentPassword === newPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["newPassword"],
          message: "New password must be different from current password",
        });
      }
    });
};

/**
 * Creates a Zod schema for validating notification preferences.
 * @returns {z.ZodObject} Validation schema for the notifications form.
 */
export const createNotificationPreferencesSchema = () => {
  return z.object({
    notifications: z
      .object({
        master: z.boolean().default(true),
        payment: z.boolean().default(true),
        messages: z.boolean().default(true),
        subscribers: z.boolean().default(true),
        earnings: z.boolean().default(true),
        payouts: z.boolean().default(true),
      })
      .partial()
      .transform((preferences) => {
        const masterPreference =
          preferences.master ?? true;

        if (!masterPreference) {
          return {
            ...preferences,
            master: false,
            payment: false,
            messages: false,
            subscribers: false,
            earnings: false,
            payouts: false,
          };
        }

        return {
          master: masterPreference,
          payment: preferences.payment ?? true,
          messages: preferences.messages ?? true,
          subscribers: preferences.subscribers ?? true,
          earnings: preferences.earnings ?? true,
          payouts: preferences.payouts ?? true,
        };
      }),
  });
};

